import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { GeoAPI, VariableEntry, GeoConfig } from "../api/endpoints";
import ProjectFormPicker from "../components/ProjectFormPicker";
import PlotlyChart, { COLORWAY } from "../components/Plot";
import {
  Card,
  ErrorBanner,
  Spinner,
} from "../components/Layout";
import type { Data, Layout } from "plotly.js-dist-min";

export default function GeoMapPage() {
  const qc = useQueryClient();
  const [projectId, setProjectId] = useState<number | null>(null);
  const [formId, setFormId] = useState<string | null>(null);

  const [gpsField, setGpsField] = useState<string>("");
  const [variable, setVariable] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [mode, setMode] = useState<"points" | "grid" | "density" | "change">("points");
  const [gridMeters, setGridMeters] = useState<number>(1000);
  const [selectedWaves, setSelectedWaves] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [wave1, setWave1] = useState<string>("");
  const [wave2, setWave2] = useState<string>("");
  const [modalConfig, setModalConfig] = useState(false);

  // Consultar campos y variables disponibles
  const fieldsQ = useQuery({
    queryKey: ["geo-fields", projectId, formId],
    queryFn: () => GeoAPI.fields(projectId!, formId!),
    enabled: !!projectId && !!formId,
  });

  // Consultar configuración de mapa y permisos
  const configQ = useQuery({
    queryKey: ["geo-config", projectId, formId],
    queryFn: () => GeoAPI.config(projectId!, formId!),
    enabled: !!projectId && !!formId,
  });

  // Auto-seleccionar primer campo GPS disponible
  const activeGpsField = gpsField || fieldsQ.data?.gps_fields?.[0] || "";

  // Determinar si la variable seleccionada es numérica o categórica
  const selectedVarObj: VariableEntry | undefined = useMemo(() => {
    return fieldsQ.data?.dictionary?.find((v) => v.name === variable);
  }, [fieldsQ.data, variable]);

  const isNumeric = useMemo(() => {
    if (!selectedVarObj) return false;
    const tipo = (selectedVarObj.type || "").split(" ")[0];
    return ["integer", "decimal", "calculate"].includes(tipo) && !selectedVarObj.choices;
  }, [selectedVarObj]);

  const isMultiple = useMemo(() => {
    return (selectedVarObj?.type || "").startsWith("select_multiple");
  }, [selectedVarObj]);

  // Consulta geográfica
  const geoQ = useQuery({
    queryKey: [
      "geo-query",
      projectId,
      formId,
      activeGpsField,
      variable,
      category,
      mode,
      gridMeters,
      selectedWaves,
      dateFrom,
      dateTo,
      wave1,
      wave2,
    ],
    queryFn: () => {
      const wavesPayload =
        mode === "change"
          ? [wave1, wave2].filter(Boolean)
          : selectedWaves.length
          ? selectedWaves
          : undefined;

      return GeoAPI.query({
        project_id: projectId!,
        form_id: formId!,
        gps_field: activeGpsField,
        variable: variable || undefined,
        category: (isMultiple || !isNumeric) && category ? category : undefined,
        mode,
        grid_meters: gridMeters,
        waves: wavesPayload,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        min_n: configQ.data?.options.min_n || 10,
      });
    },
    enabled: !!projectId && !!formId && !!activeGpsField,
  });

  // Mutation para guardar configuración
  const [configForm, setConfigForm] = useState<Partial<GeoConfig["options"]>>({});
  const saveConfigMut = useMutation({
    mutationFn: (values: Partial<GeoConfig["options"]>) =>
      GeoAPI.saveConfig(projectId!, formId!, values),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["geo-config", projectId, formId] });
      void qc.invalidateQueries({ queryKey: ["geo-query"] });
      setModalConfig(false);
    },
  });

  // Construcción de capas para Plotly Mapbox
  const { chartData, mapLayout } = useMemo(() => {
    if (!geoQ.data || !geoQ.data.features || geoQ.data.features.length === 0) {
      return {
        chartData: [] as Data[],
        mapLayout: {
          mapbox: {
            style: "open-street-map",
            center: { lat: 4.65, lon: -74.05 },
            zoom: 11,
          },
        } as Partial<Layout>,
      };
    }

    const features = geoQ.data.features;

    // Calcular centro promedio y extensión para centrar el mapa
    let minLat = 90;
    let maxLat = -90;
    let minLon = 180;
    let maxLon = -180;
    let countCoords = 0;

    const includeCoord = (lon: number, lat: number) => {
      if (typeof lon === "number" && typeof lat === "number" && !isNaN(lon) && !isNaN(lat)) {
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        countCoords++;
      }
    };

    if (geoQ.data.mode === "points") {
      features.forEach((f) => {
        const coords = (f.geometry as { coordinates: [number, number] }).coordinates;
        if (coords && coords.length >= 2) {
          includeCoord(coords[0], coords[1]);
        }
      });
    } else {
      features.forEach((f) => {
        const coords = (f.geometry as { coordinates: number[][][] }).coordinates?.[0];
        if (coords && coords.length >= 4) {
          const cLon = (coords[0][0] + coords[2][0]) / 2;
          const cLat = (coords[0][1] + coords[2][1]) / 2;
          includeCoord(cLon, cLat);
        }
      });
    }

    const centerLat = countCoords > 0 ? (minLat + maxLat) / 2 : 4.65;
    const centerLon = countCoords > 0 ? (minLon + maxLon) / 2 : -74.05;
    const spanLat = countCoords > 0 ? maxLat - minLat : 0;
    const spanLon = countCoords > 0 ? maxLon - minLon : 0;
    const maxSpan = Math.max(spanLat, spanLon);

    let zoom = 11;
    if (countCoords > 0) {
      if (maxSpan < 0.02) zoom = 14;
      else if (maxSpan < 0.05) zoom = 13;
      else if (maxSpan < 0.15) zoom = 12;
      else if (maxSpan < 0.5) zoom = 10;
      else if (maxSpan < 1.5) zoom = 8;
      else zoom = 6;
    }

    const traces: Data[] = [];

    if (geoQ.data.mode === "points") {
      if (geoQ.data.numeric) {
        // Escala numérica continua
        const lats: number[] = [];
        const lons: number[] = [];
        const values: number[] = [];
        const hoverTexts: string[] = [];

        features.forEach((f) => {
          const coords = (f.geometry as { coordinates: [number, number] }).coordinates;
          const p = f.properties;
          lons.push(coords[0]);
          lats.push(coords[1]);
          const val = typeof p.value === "number" ? p.value : 0;
          values.push(val);
          hoverTexts.push(
            `Caso: ${p.source_id || p.case_key || "—"}<br>Valor: ${p.value ?? "Sin dato"}<br>Ola: ${p.wave || "—"}<br>Fecha: ${p.date || "—"}`
          );
        });

        traces.push({
          type: "scattermapbox",
          lat: lats,
          lon: lons,
          mode: "markers",
          marker: {
            size: 9,
            color: values,
            colorscale: "Viridis",
            showscale: true,
            colorbar: {
              title: { text: selectedVarObj?.label || variable || "Valor" },
              len: 0.7,
            },
          },
          text: hoverTexts,
          hoverinfo: "text",
        } as Data);
      } else {
        // Categórico: agrupar por categoría para generar trazas con leyenda
        const groups: Record<string, { lats: number[]; lons: number[]; texts: string[] }> = {};

        features.forEach((f) => {
          const coords = (f.geometry as { coordinates: [number, number] }).coordinates;
          const p = f.properties;
          const rawCat = p.value !== null && p.value !== undefined ? String(p.value) : "Sin respuesta";
          const labelCat =
            geoQ.data?.choices?.[rawCat] || rawCat;

          if (!groups[labelCat]) {
            groups[labelCat] = { lats: [], lons: [], texts: [] };
          }
          groups[labelCat].lons.push(coords[0]);
          groups[labelCat].lats.push(coords[1]);
          groups[labelCat].texts.push(
            `Respuesta: ${labelCat}<br>Caso: ${p.source_id || "—"}<br>Ola: ${p.wave || "—"}<br>Precisión: ${p.accuracy ? p.accuracy + "m" : "desconocida"}`
          );
        });

        Object.entries(groups).forEach(([catName, grp], idx) => {
          const color = COLORWAY[idx % COLORWAY.length];
          traces.push({
            type: "scattermapbox",
            name: `${catName} (${grp.lats.length})`,
            lat: grp.lats,
            lon: grp.lons,
            mode: "markers",
            marker: {
              size: 8,
              color: color,
              opacity: 0.85,
            },
            text: grp.texts,
            hoverinfo: "text",
          } as Data);
        });
      }
    } else {
      // Modo cuadrícula o densidad: calcular centroides para marcadores y capa de polígonos
      const gridLats: number[] = [];
      const gridLons: number[] = [];
      const gridValues: number[] = [];
      const gridTexts: string[] = [];

      features.forEach((f) => {
        const coords = (f.geometry as { coordinates: number[][][] }).coordinates[0];
        if (coords && coords.length >= 4) {
          const cLon = (coords[0][0] + coords[2][0]) / 2;
          const cLat = (coords[0][1] + coords[2][1]) / 2;
          gridLons.push(cLon);
          gridLats.push(cLat);
          const p = f.properties;
          const val = typeof p.value === "number" ? p.value : 0;
          gridValues.push(val);
          gridTexts.push(
            `Casos en cuadrícula: ${p.n}<br>Valor calculado: ${typeof p.value === "number" ? p.value.toFixed(1) : "—"}${p.small_base ? "<br>⚠ Base pequeña (n < 30)" : ""}`
          );
        }
      });

      traces.push({
        type: "scattermapbox",
        lat: gridLats,
        lon: gridLons,
        mode: "markers",
        marker: {
          size: 14,
          color: gridValues,
          colorscale: "Blues",
          showscale: true,
          colorbar: {
            title: { text: mode === "density" ? "Casos" : "Valor / %" },
            len: 0.7,
          },
        },
        text: gridTexts,
        hoverinfo: "text",
      } as Data);
    }

    const layout: Partial<Layout> = {
      mapbox: {
        style: "open-street-map",
        center: { lat: centerLat, lon: centerLon },
        zoom: zoom,
      },
      margin: { t: 0, b: 0, l: 0, r: 0 },
      showlegend: geoQ.data.mode === "points" && !geoQ.data.numeric,
      legend: {
        orientation: "h",
        y: 0.02,
        x: 0.02,
        bgcolor: "rgba(255, 255, 255, 0.9)",
        bordercolor: "#e1e0d9",
        borderwidth: 1,
      },
    };

    return { chartData: traces, mapLayout: layout };
  }, [geoQ.data, variable, selectedVarObj, mode]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-tinta">Mapa Territorial y Análisis Espacial</h1>
          <p className="text-xs text-tinta-3 mt-1">
            Visualiza respuestas georreferenciadas con colores estables, cuadrículas métricas y comparativa entre olas.
          </p>
        </div>
        {configQ.data?.can_configure && (
          <button
            type="button"
            className="btn-sec text-xs"
            onClick={() => {
              setConfigForm(configQ.data.options);
              setModalConfig(true);
            }}
          >
            ⚙ Configuración GPS
          </button>
        )}
      </div>

      {/* Selector de Fuente (Encuesta o Acumulado) */}
      <Card>
        <ProjectFormPicker
          projectId={projectId}
          formId={formId}
          onProject={(pid) => {
            setProjectId(pid);
            setFormId(null);
            setVariable("");
            setGpsField("");
          }}
          onForm={(fid) => {
            setFormId(fid);
            setVariable("");
            setGpsField("");
          }}
        />
      </Card>

      {/* Controles de Capas y Variables */}
      {projectId && formId && (
        <Card>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            {/* Campo GPS */}
            <div>
              <label className="etiqueta">Campo GPS (coordenadas)</label>
              <select
                className="input"
                value={activeGpsField}
                onChange={(e) => setGpsField(e.target.value)}
                disabled={!fieldsQ.data?.gps_fields || fieldsQ.data.gps_fields.length === 0}
              >
                {fieldsQ.data?.gps_fields && fieldsQ.data.gps_fields.length > 0 ? (
                  fieldsQ.data.gps_fields.map((f) => (
                    <option key={f} value={f}>
                      {fieldsQ.data?.dictionary?.find((d) => d.name === f)?.label
                        ? `${fieldsQ.data.dictionary.find((d) => d.name === f)?.label} (${f})`
                        : f}
                    </option>
                  ))
                ) : (
                  <option value="">— Sin campo GPS disponible —</option>
                )}
              </select>
            </div>

            {/* Variable a pintar */}
            <div>
              <label className="etiqueta">Variable a representar</label>
              <select
                className="input"
                value={variable}
                onChange={(e) => {
                  setVariable(e.target.value);
                  setCategory("");
                }}
              >
                <option value="">— Ninguna (sólo ubicación) —</option>
                {fieldsQ.data?.dictionary
                  ?.filter((v) => !v.is_technical && v.name !== activeGpsField)
                  .map((v) => (
                    <option key={v.name} value={v.name}>
                      {v.label ? `${v.label} (${v.name})` : v.name}
                    </option>
                  ))}
              </select>
            </div>

            {/* Categoría si aplica */}
            {selectedVarObj && (selectedVarObj.choices || isMultiple) && (
              <div>
                <label className="etiqueta">
                  {isMultiple ? "Categoría a destacar *" : "Filtrar por categoría"}
                </label>
                <select
                  className="input"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="">{isMultiple ? "— Elegir categoría —" : "— Todas las opciones —"}</option>
                  {Object.entries(selectedVarObj.choices || {}).map(([val, lab]) => (
                    <option key={val} value={val}>
                      {lab} ({val})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Modo de mapa */}
            <div>
              <label className="etiqueta">Modo de visualización</label>
              <select
                className="input"
                value={mode}
                onChange={(e) => setMode(e.target.value as any)}
              >
                <option value="points">Puntos exactos (marcadores)</option>
                <option value="grid">Cuadrícula territorial (agrupado)</option>
                <option value="density">Densidad de muestra</option>
                <option value="change">Comparar 2 olas (diferencia)</option>
              </select>
            </div>

            {/* Tamaño de celda si es cuadrícula */}
            {mode === "grid" && (
              <div>
                <label className="etiqueta">Tamaño de celda</label>
                <select
                  className="input"
                  value={gridMeters}
                  onChange={(e) => setGridMeters(Number(e.target.value))}
                >
                  <option value={500}>500 metros (alta resolución)</option>
                  <option value={1000}>1 kilómetro (estándar urbano)</option>
                  <option value={2000}>2 kilómetros (zona ampliada)</option>
                  <option value={5000}>5 kilómetros (regional)</option>
                </select>
              </div>
            )}

            {/* Comparar olas */}
            {mode === "change" && (
              <>
                <div>
                  <label className="etiqueta">Ola de referencia (A)</label>
                  <select
                    className="input"
                    value={wave1}
                    onChange={(e) => setWave1(e.target.value)}
                  >
                    <option value="">— Elegir ola A —</option>
                    {fieldsQ.data?.waves.map((w) => (
                      <option key={w} value={w}>{w}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="etiqueta">Ola de contraste (B)</label>
                  <select
                    className="input"
                    value={wave2}
                    onChange={(e) => setWave2(e.target.value)}
                  >
                    <option value="">— Elegir ola B —</option>
                    {fieldsQ.data?.waves.map((w) => (
                      <option key={w} value={w}>{w}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* Filtro de olas en modo normal */}
            {mode !== "change" && fieldsQ.data?.waves && fieldsQ.data.waves.length > 1 && (
              <div>
                <label className="etiqueta">Filtrar por ola</label>
                <select
                  className="input"
                  value={selectedWaves[0] || ""}
                  onChange={(e) => setSelectedWaves(e.target.value ? [e.target.value] : [])}
                >
                  <option value="">Todas las olas</option>
                  {fieldsQ.data.waves.map((w) => (
                    <option key={w} value={w}>{w}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Rango de fechas */}
            <div>
              <label className="etiqueta">Fecha desde</label>
              <input
                type="date"
                className="input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="etiqueta">Fecha hasta</label>
              <input
                type="date"
                className="input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Visor Cartográfico y Resumen */}
      {projectId && formId && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Mapa Principal */}
          <div className="lg:col-span-3">
            <Card>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold text-tinta">
                    {variable ? `Distribución: ${selectedVarObj?.label || variable}` : "Ubicación de entrevistas"}
                  </div>
                  {geoQ.isFetching && (
                    <span className="text-xs text-marca flex items-center gap-1">
                      <Spinner /> Cargando coordenadas…
                    </span>
                  )}
                </div>

                {!activeGpsField && fieldsQ.isSuccess && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                    ⚠ No se detectó ninguna variable de tipo geopoint o start-geopoint en esta encuesta o corte acumulado. Verifique que el formulario contenga captura de GPS.
                  </div>
                )}

                {geoQ.error && (
                  <ErrorBanner message={(geoQ.error as Error).message} />
                )}

                {geoQ.data?.warning && (
                  <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                    ⚠ {geoQ.data.warning}
                  </div>
                )}

                <div className="h-[520px] w-full bg-plano rounded border border-grid overflow-hidden relative">
                  {geoQ.isLoading && (
                    <div className="absolute inset-0 z-10 bg-white/80 flex flex-col items-center justify-center gap-2 text-xs text-marca">
                      <Spinner />
                      <span>Cargando coordenadas y generando mapa territorial…</span>
                    </div>
                  )}

                  {!activeGpsField ? (
                    <div className="h-full flex flex-col items-center justify-center text-xs text-tinta-3 p-6 text-center">
                      <span>Esta encuesta o corte acumulado no tiene un campo GPS disponible para mapear.</span>
                    </div>
                  ) : geoQ.data && geoQ.data.features.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-xs text-tinta-3 p-6 text-center">
                      <span>No se encontraron entrevistas con coordenadas válidas para los filtros seleccionados.</span>
                    </div>
                  ) : (
                    <PlotlyChart
                      data={chartData}
                      layout={mapLayout}
                      style={{ width: "100%", height: "100%" }}
                    />
                  )}
                </div>

                {geoQ.data?.note && (
                  <p className="text-[11px] text-tinta-3 italic mt-1">
                    {geoQ.data.note}
                  </p>
                )}
              </div>
            </Card>
          </div>

          {/* Panel Lateral de Diagnóstico y Métricas */}
          <div className="space-y-4">
            <Card>
              <div className="space-y-3 text-xs">
                <h3 className="font-semibold text-tinta uppercase tracking-wide border-b border-grid pb-1.5">
                  Diagnóstico GPS
                </h3>
                {geoQ.data ? (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-tinta-2">Entrevistas ubicadas:</span>
                      <span className="font-bold text-estado-ok">
                        {geoQ.data.counts.located ?? 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-tinta-2">Coordenadas ausentes:</span>
                      <span className="font-medium text-tinta-3">
                        {geoQ.data.counts.missing ?? 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-tinta-2">Coordenadas inválidas:</span>
                      <span className="font-medium text-estado-critico">
                        {geoQ.data.counts.invalid ?? 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-tinta-2">Sospechosas (0, 0):</span>
                      <span className="font-medium text-estado-alerta">
                        {geoQ.data.counts.suspicious ?? 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-tinta-2">Puntos repetidos exactos:</span>
                      <span className="font-medium text-tinta">
                        {geoQ.data.counts.repeated_locations ?? 0}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-tinta-3 py-2">Sin datos disponibles.</div>
                )}
              </div>
            </Card>

            {/* Desglose por Categoría si existe */}
            {geoQ.data?.categories && Object.keys(geoQ.data.categories).length > 0 && (
              <Card>
                <div className="space-y-2.5 text-xs">
                  <h3 className="font-semibold text-tinta uppercase tracking-wide border-b border-grid pb-1.5">
                    Recuento por categoría
                  </h3>
                  <div className="space-y-1.5">
                    {Object.entries(geoQ.data.categories).map(([catVal, cnt]) => {
                      const label = geoQ.data?.choices?.[catVal] || catVal;
                      const pct = geoQ.data?.counts?.located
                        ? ((cnt / geoQ.data.counts.located) * 100).toFixed(1)
                        : "0";
                      return (
                        <div key={catVal} className="flex justify-between items-center">
                          <span className="text-tinta truncate max-w-[140px]" title={label}>
                            {label}:
                          </span>
                          <span className="font-medium text-tinta-2">
                            {cnt} ({pct}%)
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Modal de Configuración GPS */}
      {modalConfig && configQ.data && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 border border-grid space-y-4">
            <div className="flex justify-between items-center border-b border-grid pb-2">
              <h3 className="font-bold text-sm text-tinta">Configuración de Mapas y Teselas</h3>
              <button
                type="button"
                className="text-xs text-tinta-3 hover:text-tinta"
                onClick={() => setModalConfig(false)}
              >
                ✕ Cerrar
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="etiqueta">URL de servidor de teselas (HTTPS)</label>
                <input
                  type="text"
                  className="input"
                  value={configForm.tiles_url ?? configQ.data.options.tiles_url}
                  onChange={(e) => setConfigForm({ ...configForm, tiles_url: e.target.value })}
                />
              </div>

              <div>
                <label className="etiqueta">Atribución cartográfica</label>
                <input
                  type="text"
                  className="input"
                  value={configForm.attribution ?? configQ.data.options.attribution}
                  onChange={(e) => setConfigForm({ ...configForm, attribution: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="etiqueta">Límite de puntos en mapa</label>
                  <input
                    type="number"
                    className="input"
                    value={configForm.point_limit ?? configQ.data.options.point_limit}
                    onChange={(e) => setConfigForm({ ...configForm, point_limit: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="etiqueta">Umbral base pequeña (min n)</label>
                  <input
                    type="number"
                    className="input"
                    value={configForm.min_n ?? configQ.data.options.min_n}
                    onChange={(e) => setConfigForm({ ...configForm, min_n: Number(e.target.value) })}
                  />
                </div>
              </div>
            </div>

            <div className="pt-3 border-t border-grid flex justify-end gap-2 text-xs">
              <button
                type="button"
                className="btn-sec"
                onClick={() => setModalConfig(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => saveConfigMut.mutate(configForm)}
                disabled={saveConfigMut.isPending}
              >
                {saveConfigMut.isPending ? "Guardando…" : "Guardar ajustes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
